import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Play, Download, Crown } from "lucide-react";
import { getCreatedInvitationsRequest, updateCreatedInvitationInfoRequest } from "../store/slice/appSlice";
import ApiService from "../services/AppService";
import { GENERATE_VIDEO_STREAM } from "../config/endpoints";
import VideoPreviewModal from "../common/VideoPreviewModal";

export default function GeneratedVideoPage() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("free");
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("idle");
  const [paymentError, setPaymentError] = useState("");

  const [generationStatus, setGenerationStatus] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const { loginData, user } = useSelector((state) => state.auth);

  const {
    getCreatedInvitationsLoading,
    getCreatedInvitationsData,
    createdRemoveInvitationsAfter,
    createdFreeInvitationsTotal,
    createdPaidInvitationsTotal,
  } = useSelector((state) => state.app);

  useEffect(() => {
    dispatch(getCreatedInvitationsRequest({ type: activeTab, page: 1 }));
  }, [dispatch, activeTab]);

  const invitations = getCreatedInvitationsData?.data || [];
  const pagination = getCreatedInvitationsData || {};

  const handlePageChange = (page) => {
    dispatch(getCreatedInvitationsRequest({ type: activeTab, page }));
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePremiumPayment = async (item) => {
    if (paymentLoading) return;

    try {
      setPaymentLoading(true);
      setPaymentStatus("processing");
      setPaymentError("");

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        setPaymentStatus("failed");
        setPaymentError("Payment SDK failed to load.");
        setPaymentLoading(false);
        return;
      }

      const orderData = await ApiService.createInvitationOrder({
        invitation_id: item.id,
      });

      if (!orderData?.success) {
        setPaymentStatus("failed");
        setPaymentError(orderData?.message || "Order creation failed.");
        setPaymentLoading(false);
        return;
      }

      const options = {
        key: orderData.data.key,
        amount: orderData.data.amount,
        currency: orderData.data.currency,
        name: "GI9 Invitations",
        description: item?.template?.title,
        order_id: orderData.data.order_id,

        handler: async function (response) {
          try {
            const verifyData =
              await ApiService.verifyInvitationPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                invitation_id: item.id,
              });

            if (verifyData?.success) {
              setPaymentStatus("success");
              setActiveTab('paid');
              setTimeout(() => {
                dispatch(
                  getCreatedInvitationsRequest({
                    type: "paid",
                    page: 1,
                  })
                );

                setShowPremiumModal(false);
                setPaymentStatus("idle");
              }, 1500);
            } else {
              setPaymentStatus("failed");
              setPaymentError("Payment verification failed.");
            }
          } catch (err) {
            // ✅ Mark as failed if verification fails
            await ApiService.invitationPaymentFailed({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              error: err.message,
            });
            
            setPaymentStatus("failed");
            setPaymentError("Verification failed.");
          } finally {
            setPaymentLoading(false);
          }
        },
        modal: {
          ondismiss: async function () {
            setPaymentLoading(false);
            setPaymentStatus("idle");

            // ✅ CALL FAILED API (mark as failed/abandoned)
            try {
              await ApiService.invitationPaymentFailed({
                razorpay_order_id: orderData.data.order_id,
                error: "User closed payment popup",
              });
            } catch (err) {
              console.error("Dismiss failed:", err);
            }
          },
        },
        theme: {
          color: "#F59E0B",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", async function (response) {
        console.log('--payment failed--',response);
        setPaymentLoading(false);
        setPaymentStatus("failed");

        const errorMsg = response?.error?.description || "Payment failed.";
        setPaymentError(errorMsg);

        // ✅ CALL BACKEND (IMPORTANT)
        try {
          await ApiService.failedInvitationPayment({
            razorpay_order_id: response?.error?.metadata?.order_id,
            razorpay_payment_id: response?.error?.metadata?.payment_id || null,
            error: response?.error,
          });
        } catch (err) {
          console.error("Failed API error:", err);
        }
      });

      razorpay.open();

    } catch (err) {
      console.error(err);
      setPaymentStatus("failed");
      setPaymentError("Something went wrong.");
      setPaymentLoading(false);
    }
  };

  const handlePremiumDownload = (item) => {
    // If premium file already exists → download directly
    if (item?.invitation_file_premium_url) {
      window.open(item.invitation_file_premium_url, "_blank");
      return;
    }

    // Otherwise generate premium video
    setIsGenerating(true);
    setGeneratingId(item.id);
    setGenerationStatus("Initializing premium video generation...");

    // generate video
    listenToProgress(item.id, item);
  };

  const listenToProgress = (jobId, item) => {
    const es = new EventSource(
      `${GENERATE_VIDEO_STREAM}/${jobId}?token=${loginData?.token || "none"}`
    );

    es.onmessage = (e) => {
      const event = JSON.parse(e.data);

      if (event.event === "progress") {
        setGenerationStatus(event.message);
      }

      if (event.event === "completed") {
        setGenerationStatus("Video Ready ✅");
        setIsGenerating(false);

        // SAVE IN DB
        dispatch(
          updateCreatedInvitationInfoRequest({
            id: event.data.job_id,
            invitation_file: event.data.key,
            storage_driver: event.data.storage_type,
            with_watermark: event.data.with_watermark
          })
        );

        // Refresh list
        dispatch(
          getCreatedInvitationsRequest({
            type: activeTab,
            page: pagination.current_page || 1,
          })
        );

        // Auto download
        window.open(event.data.video, "_blank");

        es.close();
      }

      if (event.event === "error") {
        setGenerationStatus(`Error: ${event.message}`);
        setIsGenerating(false);
        es.close();
      }
    };

    es.onerror = () => {
      setGenerationStatus("Connection lost.");
      setIsGenerating(false);
      es.close();
    };
  };

  const handleFreeDownloadClick = (item) => {
    const freeUrl = item?.invitation_file_url || item?.invitation_file_premium_url;

    // for testing purpose
    if (user?.mobile == '6378005100') {
      handlePremiumDownload(item);
      console.log('trigger handleFreeDownloadClick->handlePremiumDownload');
      return;
    }

    // Check if the page is loaded inside the React Native WebView
    if (window.ReactNativeWebView) {
      // Send a message to React Native
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "SHOW_AD_FOR_DOWNLOAD",
        url: freeUrl
      }));
    } else {
      // Fallback: If opened in a normal mobile/desktop browser, just download
      window.open(freeUrl, "_blank");
    }
  };

  // Listen for the signal from React Native that the Ad is finished
  useEffect(() => {
    const handleNativeEvent = (event) => {
      if (event.detail && event.detail.type === "PROCEED_DOWNLOAD") {
        window.open(event.detail.url, "_blank");
      }
    };
    
    window.addEventListener("NATIVE_MESSAGE", handleNativeEvent);
    return () => window.removeEventListener("NATIVE_MESSAGE", handleNativeEvent);
  }, []);


  /* =========================
      RENDER LIST FUNCTION
  ==========================*/
  const renderList = () => {
    if (getCreatedInvitationsLoading) {
      return (
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (!invitations.length) {
      return (
        <div className="text-center py-20 bg-white/50 rounded-2xl border border-dashed border-gray-300">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
            <Play className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">No videos yet</h3>
          <p className="text-gray-500 text-sm mt-1">
            {activeTab === "paid" ? "No premium videos found." : "Create your first invitation to see it here."}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {invitations.map((item) => {
          const videoUrl =
            activeTab === "paid"
              ? (item?.invitation_file_premium_url || item?.invitation_file_url)
              : (item?.invitation_file_url || item?.invitation_file_premium_url)

          const previewFileUrl = item?.template?.preview_file_url
          const freeUrl = item?.invitation_file_url;
          const premiumUrl = item?.invitation_file_premium_url;
          const isFree = item?.is_free == 1 ? true : false;

          const price =
            item?.template?.category?.template_price || 0;
          const oldPrice =
            item?.template?.category?.template_price_old || 0;

          return (
            <div
              key={item.id}
              className="bg-white rounded-3xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl transition"
            >
              {/* VIDEO */}
              <div className="aspect-[9/16] bg-gray-100 relative">
                {previewFileUrl ? (
                  <div
                    className="aspect-[9/16] relative bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setPreviewVideo(videoUrl);
                      setPreviewTitle(item?.template?.title);
                    }}
                  >

                    {/* Thumbnail */}
                    <img
                      loading="lazy"
                      src={previewFileUrl}
                      alt={item?.template?.title}
                      className="w-full h-full object-cover"
                    />

                    {/* Play button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-black/40 rounded-full p-3">
                        <Play className="text-white w-6 h-6" />
                      </div>
                    </div>

                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-300 text-sm">
                    Loading...
                  </div>
                )}

                {activeTab === "paid" && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Crown size={12} />
                  </div>
                )}
              </div>

              {/* FOOTER */}
              <div className="p-4">
                <h3 className="text-sm font-semibold truncate text-gray-800 mb-3">
                  {item?.template?.title || "Generated Video"}
                </h3>
                {/* CATEGORY */}
                <p className="text-xs text-gray-500 mb-2">
                  {item?.template?.category?.name}
                </p>
                <div className="flex gap-2 flex-wrap mb-2">
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                    TID: {item.template_id}
                  </span>
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                    INV: {item.id}
                  </span>
                </div>

                {/* FREE TAB */}
                {activeTab === "free" ? (
                  <div className="flex flex-col gap-2">
                    {/* FREE DOWNLOAD */}
                    {/* <a
                      href={freeUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-xs bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition"
                    >
                      <Download size={14} />
                      Download Free
                    </a> */}
                    <button
                      onClick={() => handleFreeDownloadClick(item)}
                      className="flex items-center justify-center gap-2 text-xs bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition"
                    >
                      <Download size={14} />
                      Download Free
                    </button>

                    {/* PREMIUM DOWNLOAD */}
                    {!isFree && <>
                      <button
                        onClick={() => {
                          setSelectedItem(item);
                          setShowPremiumModal(true);
                        }}
                        className="flex items-center justify-center gap-2 text-xs bg-yellow-500 text-white px-4 py-2 rounded-full hover:bg-yellow-600 transition"
                      >
                        <Crown size={14} />
                        Download Premium
                      </button>
                    

                      {/* PRICE */}
                      <div className="text-center mt-1">
                        {oldPrice > 0 && (
                          <span className="text-gray-400 text-xs line-through mr-2">
                            ₹{oldPrice}
                          </span>
                        )}
                        <span className="text-yellow-600 font-bold text-sm">
                          ₹{price}
                        </span>
                      </div>
                    </>}
                  </div>
                ) : (
                  /* PREMIUM TAB */
                  <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handlePremiumDownload(item)}
                        // disabled={isGenerating && generatingId === item.id}
                        disabled={isGenerating}
                        className="flex items-center justify-center gap-2 text-xs bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition disabled:opacity-60"
                      >
                        <Download size={14} />
                        {isGenerating && generatingId === item.id
                          ? "Generating..."
                          : "Download"}
                      </button>
                      
                    {/* ✅ ADD PROGRESS STATUS HERE */}
                    {isGenerating && generatingId === item.id && (
                      <div className="text-xs text-blue-500 font-medium">
                        {generationStatus}
                      </div>
                    )}

                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* =========================
      RENDER PAGINATION
  ==========================*/
  const renderPagination = useCallback(() => {
    if (!pagination?.last_page || pagination.last_page <= 1) return null;

    return (
      <div className="flex justify-center gap-2 mt-10 flex-wrap">
        {Array.from({ length: pagination.last_page }, (_, i) => {
          const page = i + 1;
          const isActive = page === pagination.current_page;

          return (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`w-10 h-10 rounded-full text-sm font-semibold transition ${
                isActive
                  ? "bg-yellow-500 text-white"
                  : "bg-white border hover:bg-gray-100"
              }`}
            >
              {page}
            </button>
          );
        })}
      </div>
    );
  }, [pagination]);

  return (
    <div className="min-h-screen w-full bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* HEADER */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">
            My Generated Videos
          </h1>
        </div>

        {/* TABS */}
        <div className="flex gap-4 mb-8 max-w-md">
          <button
            onClick={() => setActiveTab("free")}
            className={`flex-1 py-2 rounded-full font-semibold transition ${
              activeTab === "free"
                ? "bg-green-500 text-white"
                : "bg-white border text-gray-600"
            }`}
          >
            Free ({createdFreeInvitationsTotal || 0})
          </button>

          <button
            onClick={() => setActiveTab("paid")}
            className={`flex-1 py-2 rounded-full font-semibold transition ${
              activeTab === "paid"
                ? "bg-yellow-500 text-white"
                : "bg-white border text-gray-600"
            }`}
          >
            Premium ({createdPaidInvitationsTotal || 0})
          </button>
        </div>
        {/* REMOVE AFTER DAYS NOTE */}
        {createdRemoveInvitationsAfter?.[activeTab] && (
        <p className="text-sm text-gray-500 mb-6">
          <b>Note: </b>{createdRemoveInvitationsAfter[activeTab]}
        </p>
      )}

        {/* LIST */}
        {renderList()}

        {/* PAGINATION */}
        {renderPagination()}
        
      </div>

      {/* PREMIUM MODAL */}
      {showPremiumModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">

            <button
              onClick={() => {
                setShowPremiumModal(false);
                setPaymentStatus("idle");
              }}
              className="absolute top-3 right-3 text-gray-400 hover:text-black"
            >
              ✕
            </button>

            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Premium Download
            </h2>

            {/* Template Title */}
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedItem?.template?.title}
            </h3>

            {/* ID Badges */}
            <div className="flex gap-2 mt-3 flex-wrap">
              <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                Template ID: {selectedItem?.template_id}
              </span>
              <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                Invitation ID: {selectedItem?.id}
              </span>
            </div>

            {/* Divider */}
            <div className="my-5 border-t border-gray-200"></div>

            {/* Price Section */}
            <div className="text-center mb-6">
              {selectedItem?.template?.category?.template_price_old > 0 && (
                <p className="text-gray-400 line-through text-sm">
                  ₹{selectedItem?.template?.category?.template_price_old}
                </p>
              )}
              <p className="text-3xl font-bold text-yellow-600">
                ₹{selectedItem?.template?.category?.template_price}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                One-time payment • Instant premium download
              </p>
            </div>


            {/* =========================
                PAYMENT STATES
            ========================== */}

            {paymentStatus === "idle" && (
              <button
                disabled={paymentLoading}
                onClick={() => handlePremiumPayment(selectedItem)}
                className="w-full bg-yellow-500 text-white py-3 rounded-full font-semibold hover:bg-yellow-600 transition"
              >
                Pay & Download
              </button>
            )}

            {paymentStatus === "processing" && (
              <div className="text-center py-6">
                <div className="w-10 h-10 mx-auto border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600 font-medium">
                  Processing Payment...
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Please do not close this window
                </p>
              </div>
            )}

            {paymentStatus === "success" && (
              <div className="text-center py-6">
                <div className="text-green-500 text-4xl mb-3">✔</div>
                <p className="text-lg font-semibold text-green-600">
                  Payment Successful 🎉
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Preparing your premium video...
                </p>
              </div>
            )}

            {paymentStatus === "failed" && (
              <div className="text-center py-6">
                <div className="text-red-500 text-4xl mb-3">✖</div>
                <p className="text-lg font-semibold text-red-600">
                  Payment Failed
                </p>
                <p className="text-sm text-gray-500 mt-1 mb-3">
                  {paymentError}
                </p>

                <button
                  onClick={() => {
                    setPaymentStatus("idle");
                    setPaymentError("");
                  }}
                  className="bg-gray-200 px-4 py-2 rounded-full text-sm"
                >
                  Try Again
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {previewVideo && (
        <VideoPreviewModal
          videoUrl={previewVideo}
          title={previewTitle}
          onClose={() => setPreviewVideo(null)}
        />
      )}
    </div>
  );
}
